import { TASK_STATUSES, compareOrder, orderKeyBetween } from '@nedelnik/shared';

import { STATUS_LABELS } from '../sprint/labels.js';

import type { Placement } from '../task/landed.js';
import type { Task, TaskStatus } from '@nedelnik/shared';

/**
 * Доска: три колонки и правило, куда попадает брошенная задача.
 *
 * Вычисление места вынесено из компонента нарочно. Перетаскивание проверяется
 * только руками (jsdom не двигает указатель), а вот «между кем и кем встала
 * задача» — обычная функция, и она обязана быть проверена тестом: именно здесь
 * ошибка выглядит как «карточка прыгнула не туда» и объясняется дольше всего.
 */

/** Идентификатор колонки как цели перетаскивания: он же означает «в конец». */
export const COLUMN_PREFIX = 'col:';

export function columnDroppableId(status: TaskStatus): string {
  return `${COLUMN_PREFIX}${status}`;
}

export function statusOfDroppable(id: string): TaskStatus | undefined {
  if (!id.startsWith(COLUMN_PREFIX)) return undefined;
  const status = id.slice(COLUMN_PREFIX.length);
  return (TASK_STATUSES as readonly string[]).includes(status) ? (status as TaskStatus) : undefined;
}

export interface BoardColumn {
  status: TaskStatus;
  title: string;
  tasks: Task[];
}

/**
 * Колонки в порядке движения работы: бэклог → в работе → готово.
 *
 * Порядок остаётся канбанным и на доске отличается от списка, где группы идут
 * по важности («в работе» сверху). Расхождение сознательное: доска про поток
 * слева направо, список про «чем занят сейчас». На телефоне доски нет вовсе
 * (3.1.6), так что двух порядков одновременно человек не видит.
 */
export function buildColumns(
  tasks: readonly Task[],
  /** Каким статусом показывать задачу: только что закрытая пару секунд стоит
   * в прежней колонке (3.1.8). */
  statusOf: (task: Task) => TaskStatus = (task) => task.status,
): BoardColumn[] {
  return TASK_STATUSES.map((status) => ({
    status,
    title: STATUS_LABELS[status],
    tasks: tasks.filter((task) => statusOf(task) === status).sort(compareOrder),
  }));
}

/**
 * Ключ порядка для места между соседями.
 *
 * Дробный индекс требует строго возрастающих краёв, а после конкурентных
 * вставок два соседа могут иметь одинаковый ключ (худший случай раздела 3.3
 * архитектуры). Тогда вставка идёт после обоих: порядок остаётся полным за счёт
 * тай-брейка по id, а падать посреди перетаскивания нельзя.
 */
export function orderKeyBetweenTasks(before?: Task, after?: Task): string {
  const left = before?.order_key ?? null;
  const right = after?.order_key ?? null;
  if (left !== null && right !== null && left >= right) {
    return orderKeyBetween(left, null);
  }
  return orderKeyBetween(left, right);
}

/**
 * Куда попадёт задача, брошенная на `overId`.
 *
 * `overId` — либо колонка (тогда в конец), либо другая карточка. Внутри своей
 * колонки движение вниз ставит задачу **после** той, на которую её положили:
 * иначе карточка, которую тащат к нижнему краю, останавливается на строку выше
 * и выглядит как несработавшее перетаскивание.
 */
export function planDrop(
  columns: readonly BoardColumn[],
  activeId: string,
  overId: string,
): Placement | undefined {
  const source = columns.find((column) => column.tasks.some((task) => task.id === activeId));
  if (source === undefined) return undefined;

  // Брошена на своё же место. После переезда в другую колонку во время
  // перетаскивания (`withPreview`) это обычный случай, а не пустой: задача уже
  // стоит там, куда её несли, и `@dnd-kit` называет целью её саму. Раньше
  // здесь выходило «некуда», и брошенная в новую колонку карточка уезжала
  // назад.
  if (overId === activeId) return placeInPlace(source, activeId);

  const targetStatus = statusOfDroppable(overId);
  const target =
    targetStatus === undefined
      ? columns.find((column) => column.tasks.some((task) => task.id === overId))
      : columns.find((column) => column.status === targetStatus);
  if (target === undefined) return undefined;

  const rest = target.tasks.filter((task) => task.id !== activeId);

  let index: number;
  if (targetStatus !== undefined) {
    index = rest.length;
  } else {
    const overIndex = rest.findIndex((task) => task.id === overId);
    if (overIndex < 0) return undefined;

    const sourceIndex = target.tasks.findIndex((task) => task.id === activeId);
    const overIndexBefore = target.tasks.findIndex((task) => task.id === overId);
    const movingDown = sourceIndex >= 0 && sourceIndex < overIndexBefore;
    index = movingDown ? overIndex + 1 : overIndex;
  }

  return {
    status: target.status,
    order_key: orderKeyBetweenTasks(rest[index - 1], rest[index]),
  };
}

/**
 * Место задачи там, где она сейчас стоит в колонке.
 *
 * Собственный ключ сохраняется, если он и так лежит между соседями: брошенная
 * туда же, откуда взяли, задача не должна порождать правку, которая ничего не
 * меняет, но уезжает на сервер и будит остальные устройства.
 */
function placeInPlace(column: BoardColumn, id: string): Placement | undefined {
  const index = column.tasks.findIndex((task) => task.id === id);
  const self = column.tasks[index];
  if (self === undefined) return undefined;

  const before = column.tasks[index - 1];
  const after = column.tasks[index + 1];
  const fits =
    (before === undefined || before.order_key < self.order_key) &&
    (after === undefined || self.order_key < after.order_key);

  return {
    status: column.status,
    order_key: fits ? self.order_key : orderKeyBetweenTasks(before, after),
  };
}

/** Где задача стоит, пока её несут: колонка и место в ней. */
export interface Preview {
  id: string;
  status: TaskStatus;
  index: number;
}

/**
 * Колонки с задачей, уже переставленной туда, куда её несут.
 *
 * Перестановка во время перетаскивания, а не только по броску, нужна ради
 * анимации. `@dnd-kit` по броску ведёт тень к месту самой карточки; пока
 * карточка стояла в старой колонке, тень улетала назад, а в новую колонку
 * задача попадала уже после, скачком (замечание владельца, 2026-09-13).
 * Теперь карточка переезжает, как только её занесли над другой колонкой, и
 * бросок «дотаскивает» тень на новое место.
 */
export function withPreview(
  columns: readonly BoardColumn[],
  preview: Preview | undefined,
): BoardColumn[] {
  if (preview === undefined) return [...columns];

  const task = columns.flatMap((column) => column.tasks).find((item) => item.id === preview.id);
  if (task === undefined) return [...columns];

  return columns.map((column) => {
    const rest = column.tasks.filter((item) => item.id !== preview.id);
    if (column.status !== preview.status) return { ...column, tasks: rest };

    const index = Math.min(Math.max(preview.index, 0), rest.length);
    return { ...column, tasks: [...rest.slice(0, index), task, ...rest.slice(index)] };
  });
}

/** Колонка, которой является цель, или в которой стоит задача с этим id. */
export function statusAt(columns: readonly BoardColumn[], id: string): TaskStatus | undefined {
  return (
    statusOfDroppable(id) ??
    columns.find((column) => column.tasks.some((task) => task.id === id))?.status
  );
}
